import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from 'src/app/core/services/event.service';
import { Calendar2Service } from 'src/app/pages/calendar2/services/calendar2.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-calendar-event',
  templateUrl: './calendar-event.component.html',
  styleUrls: ['./calendar-event.component.scss']
})
export class CalendarEventComponent implements OnInit {

  @Input() data!: {
    isVisit: boolean,
    contactName?: string,
    contactEmail?: string,
    contactPhone?: string,
    propertyId?: any,
    propertyLabel?: string,
    ownerMeeting?: boolean,
    eventName?: string,
    emailSubject?: string,
    emailBody?: string,
    location?: string,
  };
  @ViewChild('content', { static: true }) content!: ElementRef;
  @ViewChild('ownerMailContent', { static: true }) ownerMailContent!: ElementRef;
  eventForm!: FormGroup;
  eventTypes: any[] = [];
  submitted: boolean = false;
  ownerMailSubmitted: boolean = false;

 
  constructor(
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private calendarService: Calendar2Service,
    private translate: TranslateService,
    private eventService: EventService
  ) { }

  ngOnInit(): void {
    this.loadEventTypes();
  }

  loadEventTypes() {
    this.calendarService.getAllEventType()
      .subscribe((data) => {
        this.eventTypes = data;
        this.initForm();
        this.openEvent();
      });
  }

  get isOwnerMeetingMode(): boolean {
    return this.data?.ownerMeeting === true;
  }

  private normalizeText(value: any): string {
    return `${value ?? ''}`
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private resolveDefaultEventType(): any {
    if (this.data?.isVisit) {
      return this.eventTypes.find((type) => this.normalizeText(type?.name) === 'visit');
    }

    if (this.isOwnerMeetingMode) {
      const matchByName = this.eventTypes.find((type) => {
        const value = this.normalizeText(type?.name);
        return value.includes('meeting') || value.includes('reuniao') || value.includes('reuniao com cliente');
      });
      if (matchByName) {
        return matchByName;
      }
    }

    return null;
  }

  private isVisitEventType(eventType: any): boolean {
    if (!eventType) {
      return false;
    }
    const translate = this.normalizeText(eventType?.translate);
    const name = this.normalizeText(eventType?.name);
    return translate === 't-calendar.t-event-type-visit' || name === 'visit' || name.includes('visita');
  }

  initForm() {
    const defaultType = this.resolveDefaultEventType();
    const today = new Date();
    today.setHours(today.getHours() + 1, 0, 0, 0);
    const oneHour = new Date(today);
    oneHour.setHours(oneHour.getHours() + 1);

    const defaultName = this.data?.eventName
      ? this.data.eventName
      : this.data?.isVisit
        ? 'Visita'
        : this.isOwnerMeetingMode
          ? `Reuniao com proprietario${this.data?.contactName ? ` - ${this.data.contactName}` : ''}`
          : '';

    this.eventForm = this.formBuilder.group({
      type: [defaultType?.id ?? null, [Validators.required]],
      name: [defaultName, [Validators.required]],
      date: [today, [Validators.required]],
      startHour: [today, [Validators.required]],
      endHour: [oneHour, [Validators.required]],
      location: [this.data?.location ?? null],
      description: [
        this.isOwnerMeetingMode
          ? `Reuniao de alinhamento com o proprietario${this.data?.contactName ? ` ${this.data.contactName}` : ''}.`
          : null,
      ],
      isPublic: [false],
    });

    if (this.isOwnerMeetingMode) {
      this.eventForm.addControl(
        'emailSubject',
        this.formBuilder.control(
          this.data?.emailSubject ?? `Obrigado pela preferencia`,
          [Validators.required],
        ),
      );
      this.eventForm.addControl(
        'emailBody',
        this.formBuilder.control(
          this.data?.emailBody ??
          `Ola ${this.data?.contactName ?? ''},\n\nObrigado pela preferencia.\n\nAte breve,`,
          [Validators.required],
        ),
      );
    }

    if (this.isVisitEventType(defaultType)) {
      if (!this.eventForm.contains('contactEmail')) {
        this.eventForm.addControl(
          'contactEmail',
          this.formBuilder.control(this.data?.contactEmail, [Validators.required, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")])
        );
      }
      if (!this.eventForm.contains('property')) {
        this.eventForm.addControl('property', this.formBuilder.control(this.data?.propertyId, Validators.required));
      }
    }
  }

  onChangeDate(e: any) {
    this.eventForm.patchValue({ startHour: null });
    this.eventForm.patchValue({ endHour: null });
  }

  onChangeEventType(event: any) {
    if (event?.id) {
      const eventType = this.eventTypes.find((et: any) => {
        return et.id == event.id;
      });
      if (eventType?.name && !this.isOwnerMeetingMode) {
        this.eventForm.patchValue({ name: eventType.name });
      }
      if (this.isVisitEventType(eventType)) {

        if (!this.eventForm.contains('contactEmail')) {
          this.eventForm.addControl(
            'contactEmail',
            this.formBuilder.control(this.data?.contactEmail, [Validators.required, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")])
          );
        }
        if (!this.eventForm.contains('property')) {
          this.eventForm.addControl('property', this.formBuilder.control(this.data?.propertyId, Validators.required));
        }

      } else {
        if (this.eventForm.contains('contactEmail')) {
          this.eventForm.removeControl('contactEmail');
        }
        if (this.eventForm.contains('property')) {
          this.eventForm.removeControl('property');
        }
      }
    } else {
      if (this.eventForm.contains('contactEmail')) {
        this.eventForm.removeControl('contactEmail');
      }
      if (this.eventForm.contains('property')) {
        this.eventForm.removeControl('property');
      }
      this.eventForm.patchValue({ name: null });
    }


  }

  get form() {
    return this.eventForm.controls;
  }

  openEvent() {
    this.modalService.open(this.content, {
      size: this.isOwnerMeetingMode ? 'lg' : 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      backdropClass: 'modal-z',
      windowClass: 'modal-z'
    });
  }

  openOwnerMailEditor() {
    this.modalService.dismissAll();
    this.modalService.open(this.ownerMailContent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      backdropClass: 'modal-z',
      windowClass: 'modal-z',
    });
  }

  validRange() {
    const start: Date = this.eventForm?.get("startHour")?.value;
    const end: Date = this.eventForm.get("endHour")?.value;
    if (start && end) {
      const isValid = (end.getTime() - start.getTime() > 0);
      return isValid ? false : true;
    } else
      return false;
  }

  private buildEventPayload() {
    let payLoad: any = {};
    const rawValues = this.eventForm.getRawValue();

    const dataMail = {
      clientMail : this.data?.contactEmail,
      clientName: this.data?.contactName,
      clientPhone: this.data?.contactPhone,
      property: this.data?.propertyId,
      ownerMeeting: this.isOwnerMeetingMode,
      customMailSubject: rawValues?.emailSubject,
      customMailBody: rawValues?.emailBody,
    };

    Object.entries(rawValues).forEach(([key, val]: any) => {
      if (key === 'emailSubject' || key === 'emailBody') {
        return;
      }

      if (val instanceof Date && rawValues?.date instanceof Date && (key === 'startHour' || key === 'endHour')) {
        let strDateKey = key === 'startHour' ? 'start' : 'end';
        let d = rawValues.date.getDate();
        let m = rawValues.date.getMonth();
        let y = rawValues.date.getFullYear();
        const date = new Date(y, m, d, val.getHours(), val.getMinutes());
        payLoad[strDateKey] = date;
      } else if (key === 'isPublic' && (val === false || val === true)) {
        payLoad[key] = val;
      } else if (val && !(val instanceof Date)) {
        payLoad[key] = key == 'property' ? (val?.id ?? val) : val;
      }

    });

    return { payLoad, dataMail };
  }

  private submitEvent() {
    const { payLoad, dataMail } = this.buildEventPayload();

    this.calendarService.create(payLoad, dataMail)
      .subscribe(
        () => {
          Swal.fire(
            `${this.translate.instant('t-calendar.t-create-event')}`,
            `${this.translate.instant('t-calendar.t-event-has-been-created')}.`,
            'success'
          );
          this.modalService.dismissAll();
          this.eventForm.reset();
          this.eventService.broadcast('calendar-closed');
        },
        () => { Swal.fire(`${this.translate.instant('t-calendar.t-error-on-create-event')}`, '', 'error'); },
        () => { }
      );
  }

  saveEvent() {
    this.submitted = true;
    if (!this.eventForm.valid) {
      return;
    }

    if (this.isOwnerMeetingMode) {
      this.openOwnerMailEditor();
      return;
    }

    this.submitEvent();
  }

  confirmOwnerMailAndSave() {
    this.ownerMailSubmitted = true;
    const subject = `${this.eventForm.get('emailSubject')?.value ?? ''}`.trim();
    const body = `${this.eventForm.get('emailBody')?.value ?? ''}`.trim();
    if (!subject || !body) {
      this.eventForm.get('emailSubject')?.markAsTouched();
      this.eventForm.get('emailBody')?.markAsTouched();
      this.eventForm.patchValue({
        emailSubject: subject,
        emailBody: body,
      });
      return;
    }

    this.submitEvent();
  }

  backToEventModal() {
    this.ownerMailSubmitted = false;
    this.modalService.dismissAll();
    this.openEvent();
  }

  closeModal() {
    this.modalService.dismissAll();
    this.eventForm.reset();
    this.eventService.broadcast('calendar-closed');
  }

}
